import React, { useState, useEffect, useCallback } from 'react';
import { clientAPI, productAPI, categoryAPI, techniqueAPI, settingsAPI, blogAPI, emailTemplateAPI } from '../../services/api';
import { Button, Modal, StatusBadge, Pagination, ConfirmDialog, EmptyState, FormField, Input, Textarea, Select, PasswordInput } from '../../components/common';
import { toast } from 'react-toastify';

/* ════════════════════════════════════════════════════
   MODAL ENVOI EMAIL CLIENT
   ════════════════════════════════════════════════════ */
const ClientSendEmailModal = ({ isOpen, onClose, client }) => {
  const [templates, setTemplates] = useState([]);
  const [tplId,     setTplId]     = useState('');
  const [vars,      setVars]      = useState({});
  const [sending,   setSending]   = useState(false);
  const selectedTpl = templates.find(t => String(t.id) === String(tplId));

  useEffect(() => {
    if (!isOpen) return;
    emailTemplateAPI.list()
      .then(({ data }) => {
        const all = data.data || [];
        setTemplates(all.filter(t => t.is_active && ['client','general','newsletter'].includes(t.category)));
        setTplId(''); setVars({});
      })
      .catch(() => {});
  }, [isOpen]);

  useEffect(() => {
    if (!selectedTpl || !client) return;
    const auto = {};
    (selectedTpl.variables || []).forEach(v => {
      if      (v === 'client_name')    auto[v] = client.contact_name || client.company || '';
      else if (v === 'client_email')   auto[v] = client.email || '';
      else if (v === 'company')        auto[v] = client.company || '';
      else if (v === 'client_phone')   auto[v] = client.phone || '';
    });
    setVars(auto);
  }, [tplId, selectedTpl, client]);

  const send = async () => {
    if (!tplId)            { toast.error('Choisissez un template'); return; }
    if (!client?.email)    { toast.error('Ce client n\'a pas d\'email'); return; }
    setSending(true);
    try {
      await emailTemplateAPI.send(parseInt(tplId), { recipients: [client.email], variables: vars });
      toast.success('Email envoyé ✓');
      onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur envoi'); }
    finally { setSending(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`📤 Envoyer un email à ${client?.contact_name || client?.company || ''}`} size="lg"
      footer={<div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
        <Button variant="ghost" onClick={onClose}>Annuler</Button>
        <Button variant="primary" loading={sending} onClick={send}>📤 Envoyer</Button>
      </div>}
    >
      <div style={{display:'flex',flexDirection:'column',gap:16}}>
        <div style={{padding:'10px 14px',background:'rgba(59,130,246,.05)',border:'1px solid rgba(59,130,246,.15)',borderRadius:8,fontSize:13}}>
          Destinataire : <strong>{client?.email}</strong>
        </div>
        <FormField label="Template email" required>
          <Select value={tplId} onChange={e => setTplId(e.target.value)}>
            <option value="">-- Choisir un template --</option>
            {templates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.category})</option>)}
          </Select>
        </FormField>
        {selectedTpl && (selectedTpl.variables || []).length > 0 && (
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            <div style={{fontSize:12,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase'}}>Variables du template</div>
            {(selectedTpl.variables || []).map(v => (
              <FormField key={v} label={`{{${v}}}`}>
                <Input value={vars[v] || ''} onChange={e => setVars(p => ({...p,[v]:e.target.value}))} placeholder={`Valeur pour ${v}`} />
              </FormField>
            ))}
          </div>
        )}
        {selectedTpl && (
          <div style={{padding:'10px 14px',background:'var(--bg-page)',borderRadius:8,fontSize:13}}>
            <div style={{fontWeight:700,marginBottom:4}}>Sujet : {selectedTpl.subject}</div>
            {selectedTpl.body && <div style={{color:'var(--text-muted)',fontSize:11,fontFamily:'monospace',whiteSpace:'pre-wrap',maxHeight:120,overflow:'auto'}}>{selectedTpl.body.replace(/<[^>]+>/g,'').slice(0,300)}{selectedTpl.body.length > 300 ? '…' : ''}</div>}
          </div>
        )}
      </div>
    </Modal>
  );
};

/* ════════════════════════════════════════════════════
   CLIENTS PAGE
   ════════════════════════════════════════════════════ */
export const AdminClientsPage = () => {
  const [clients, setClients]  = useState([]);
  const [meta,    setMeta]     = useState(null);
  const [page,    setPage]     = useState(1);
  const [search,  setSearch]   = useState('');
  const [loading, setLoading]  = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [form,      setForm]      = useState({company:'',contact_name:'',email:'',phone:'',address:'',city:'',zip_code:'',country:'FR',siret:'',vat_number:'',notes:''});
  const [saving,    setSaving]    = useState(false);
  const [confirm,   setConfirm]   = useState(null);
  const [emailModal,  setEmailModal]  = useState(false);
  const [emailClient, setEmailClient] = useState(null);

  const fetch = useCallback(()=>{
    setLoading(true);
    clientAPI.list({page,per_page:16,search}).then(({data})=>{setClients(data.data||[]);setMeta(data.meta);}).catch(()=>{}).finally(()=>setLoading(false));
  },[page,search]);

  useEffect(()=>{fetch();},[fetch]);

  const openNew   = ()=>{ setEditing(null); setForm({company:'',contact_name:'',email:'',phone:'',address:'',city:'',zip_code:'',country:'FR',siret:'',vat_number:'',notes:''}); setEditModal(true); };
  const openEdit  = (c)=>{ setEditing(c); setForm(c); setEditModal(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await clientAPI.update(editing.id, form);
      else         await clientAPI.create(form);
      toast.success(editing?'Client mis à jour':'Client créé');
      setEditModal(false);
      fetch();
    } catch(err) { toast.error(err.response?.data?.message||'Erreur'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await clientAPI.delete(id); toast.success('Client supprimé'); fetch(); } catch { toast.error('Erreur'); }
    setConfirm(null);
  };

  return (
    <div style={{display:'flex',flexDirection:'column',flex:1}}>
      <div className="admin-page-header">
        <div><h1 className="admin-page-title">Clients</h1><p className="admin-page-subtitle">{meta?.total||0} clients enregistrés</p></div>
        <Button variant="primary" onClick={openNew}>+ Nouveau client</Button>
      </div>
      <div className="admin-page-content">
        <div className="admin-card">
          <div className="admin-card-header">
            <div className="search-bar" style={{maxWidth:300}}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input className="form-input" placeholder="Nom, email, SIRET…" value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}} style={{paddingLeft:36}}/>
            </div>
          </div>
          {loading ? <div className="page-loader"><div className="spinner"/></div> :
            !clients.length ? <EmptyState icon="👥" title="Aucun client"/> :
            <table className="data-table">
              <thead><tr><th>Entreprise</th><th>Contact</th><th>Email</th><th>Téléphone</th><th>Devis</th><th>Factures</th><th>Solde</th><th></th></tr></thead>
              <tbody>
                {clients.map(c=>(
                  <tr key={c.id}>
                    <td style={{fontWeight:700}}>{c.company}</td>
                    <td>{c.contact_name}</td>
                    <td style={{fontSize:13,color:'var(--text-muted)'}}><a href={`mailto:${c.email}`} style={{color:'var(--brand-accent)'}}>{c.email}</a></td>
                    <td style={{fontSize:13}}>{c.phone||'—'}</td>
                    <td style={{fontSize:13}}>{c.quote_count||0}</td>
                    <td style={{fontSize:13}}>{c.invoice_count||0}</td>
                    <td style={{fontWeight:700,color:parseFloat(c.balance||0)>=0?'var(--success)':'var(--error)'}}>{parseFloat(parseFloat(c.balance) || 0).toFixed(2)} €</td>
                    <td>
                      <div style={{display:'flex',gap:6}}>
                        <Button size="sm" variant="secondary" onClick={()=>openEdit(c)}>Modifier</Button>
                        {c.email && <Button size="sm" variant="secondary" onClick={()=>{setEmailClient(c);setEmailModal(true);}}>📤</Button>}
                        <Button size="sm" variant="ghost" onClick={()=>setConfirm(c.id)}>🗑</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
          <div style={{padding:'var(--space-md) var(--space-lg)',borderTop:'1px solid var(--border)'}}><Pagination meta={meta} onPageChange={setPage}/></div>
        </div>
      </div>

      <Modal isOpen={editModal} onClose={()=>setEditModal(false)} title={editing?'Modifier le client':'Nouveau client'} size="lg"
        footer={<div style={{display:'flex',gap:8,justifyContent:'flex-end',flexWrap:'wrap'}}>
          <Button variant="ghost" onClick={()=>setEditModal(false)}>Annuler</Button>
          {form.status !== 'published' && (
            <Button variant="secondary" loading={saving} onClick={async()=>{
              setSaving(true);
              try {
                const payload = {...form, status:'published', tags: typeof form.tags==='string' ? form.tags.split(',').map(t=>t.trim()).filter(Boolean) : (form.tags||[])};
                if(editing) await blogAPI.update(editing.id, payload);
                else await blogAPI.create(payload);
                toast.success('Article publié ✓');
                setEditModal(false); fetch();
              } catch(err){ toast.error(err.response?.data?.message||'Erreur publication'); }
              finally{ setSaving(false); }
            }}>🌐 Publier</Button>
          )}
          <Button variant="primary" loading={saving} onClick={save}>💾 Enregistrer</Button>
        </div>}
      >
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'var(--space-md)'}}>
          {[['Entreprise','company','Nom de l\'entreprise',true],['Contact','contact_name','Nom du contact',true],['Email','email','email@exemple.fr',true],['Téléphone','phone','+33 6 …',false],['Adresse','address','',false],['Ville','city','',false],['Code postal','zip_code','',false],['SIRET','siret','',false]].map(([l,k,ph,req])=>(
            <FormField key={k} label={l} required={req}>
              <Input value={form[k]||''} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))} placeholder={ph}/>
            </FormField>
          ))}
          <div style={{gridColumn:'1/-1'}}>
            <FormField label="Notes internes"><Textarea value={form.notes||''} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} rows={2}/></FormField>
          </div>
        </div>
      </Modal>
      <ConfirmDialog isOpen={!!confirm} onClose={()=>setConfirm(null)} onConfirm={()=>del(confirm)} title="Supprimer le client" message="Toutes les données associées seront supprimées." confirmLabel="Supprimer" danger/>
      <ClientSendEmailModal isOpen={emailModal} onClose={()=>{setEmailModal(false);setEmailClient(null);}} client={emailClient} />
    </div>
  );
};

/* ════════════════════════════════════════════════════
   PRODUCTS PAGE
   ════════════════════════════════════════════════════ */
export const AdminProductsPage = () => {
  const [products,   setProducts]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [techniques, setTechniques] = useState([]);
  const [meta,       setMeta]       = useState(null);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [editModal,  setEditModal]  = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [confirm,    setConfirm]    = useState(null);

  const emptyForm = {name:'',reference:'',category_id:'',description:'',short_desc:'',base_price:'',faces_count:2,is_published:false,is_featured:false,brand:'',material:'',available_sizes:['XS','S','M','L','XL','XXL'],available_colors:[],technique_ids:[],sort_order:0};
  const [form, setForm] = useState(emptyForm);

  const fetch = useCallback(()=>{
    setLoading(true);
    Promise.all([productAPI.list({page,per_page:16}),categoryAPI.list(),techniqueAPI.list()])
      .then(([p,c,t])=>{setProducts(p.data.data||[]);setMeta(p.data.meta);setCategories(c.data.data||[]);setTechniques(t.data.data||[]);})
      .catch(()=>{}).finally(()=>setLoading(false));
  },[page]);

  useEffect(()=>{fetch();},[fetch]);

  const openNew  = ()=>{ setEditing(null); setForm(emptyForm); setEditModal(true); };
  const openEdit = (p)=>{ setEditing(p); setForm({...p,technique_ids:(p.techniques||[]).map(t=>t.id)}); setEditModal(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await productAPI.update(editing.id,form);
      else         await productAPI.create(form);
      toast.success(editing?'Mis à jour':'Produit créé');
      setEditModal(false); fetch();
    } catch(err){ toast.error(err.response?.data?.message||'Erreur'); }
    finally { setSaving(false); }
  };

  const togglePublish = async (p) => {
    try { await productAPI.togglePublish(p.id); toast.success(p.is_published?'Dépublié':'Publié'); fetch(); } catch { toast.error('Erreur'); }
  };

  const del = async (id) => {
    try { await productAPI.delete(id); toast.success('Supprimé'); fetch(); } catch { toast.error('Erreur'); }
    setConfirm(null);
  };

  return (
    <div style={{display:'flex',flexDirection:'column',flex:1}}>
      <div className="admin-page-header">
        <div><h1 className="admin-page-title">Produits</h1><p className="admin-page-subtitle">{meta?.total||0} produits</p></div>
        <Button variant="primary" onClick={openNew}>+ Nouveau produit</Button>
      </div>
      <div className="admin-page-content">
        <div className="admin-card">
          {loading ? <div className="page-loader"><div className="spinner"/></div> :
            !products.length ? <EmptyState icon="👕" title="Aucun produit" action={<Button variant="primary" onClick={openNew}>Créer un produit</Button>}/> :
            <table className="data-table">
              <thead><tr><th>Produit</th><th>Catégorie</th><th>Réf.</th><th>Prix de base</th><th>Faces</th><th>Publié</th><th></th></tr></thead>
              <tbody>
                {products.map(p=>(
                  <tr key={p.id}>
                    <td>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        {p.main_image?<img src={p.main_image} alt="" style={{width:40,height:40,borderRadius:'var(--radius-sm)',objectFit:'cover'}}/>:<div style={{width:40,height:40,borderRadius:'var(--radius-sm)',background:'var(--bg-page)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18}}>👕</div>}
                        <div><div style={{fontWeight:700,fontSize:14}}>{p.name}</div>{p.is_featured&&<span style={{fontSize:10,fontWeight:700,color:'var(--brand-gold)',textTransform:'uppercase'}}>⭐ À la une</span>}</div>
                      </div>
                    </td>
                    <td style={{fontSize:13}}>{p.category_name}</td>
                    <td><code style={{fontSize:12,background:'var(--bg-page)',padding:'2px 6px',borderRadius:4}}>{p.reference}</code></td>
                    <td style={{fontWeight:600}}>{parseFloat(parseFloat(p.base_price) || 0).toFixed(2)} €</td>
                    <td style={{textAlign:'center'}}>{p.faces_count}</td>
                    <td><button onClick={()=>togglePublish(p)} style={{padding:'4px 10px',borderRadius:'var(--radius-full)',fontSize:11,fontWeight:700,border:'none',cursor:'pointer',background:p.is_published?'var(--success-bg)':'var(--bg-page)',color:p.is_published?'#065f46':'var(--text-muted)',border:`1px solid ${p.is_published?'#a7f3d0':'var(--border)'}` }}>{p.is_published?'✓ Publié':'Brouillon'}</button></td>
                    <td><div style={{display:'flex',gap:6}}>
                      {p.status !== 'published' && (
                        <Button size="sm" variant="primary" onClick={async()=>{
                          try {
                            await blogAPI.update(p.id, {...p, tags: p.tags||[], status:'published'});
                            toast.success('Article publié ✓');
                            fetch();
                          } catch(e){ toast.error('Erreur'); }
                        }}>🌐 Publier</Button>
                      )}
                      <Button size="sm" variant="secondary" onClick={()=>openEdit(p)}>Modifier</Button>
                      <Button size="sm" variant="ghost" onClick={()=>setConfirm(p.id)}>🗑</Button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
          <div style={{padding:'var(--space-md) var(--space-lg)',borderTop:'1px solid var(--border)'}}><Pagination meta={meta} onPageChange={setPage}/></div>
        </div>
      </div>

      <Modal isOpen={editModal} onClose={()=>setEditModal(false)} title={editing?'Modifier le produit':'Nouveau produit'} size="xl"
        footer={<div style={{display:'flex',gap:8,justifyContent:'flex-end',flexWrap:'wrap'}}>
          <Button variant="ghost" onClick={()=>setEditModal(false)}>Annuler</Button>
          {form.status !== 'published' && (
            <Button variant="secondary" loading={saving} onClick={async()=>{
              setSaving(true);
              try {
                const payload = {...form, status:'published', tags: typeof form.tags==='string' ? form.tags.split(',').map(t=>t.trim()).filter(Boolean) : (form.tags||[])};
                if(editing) await blogAPI.update(editing.id, payload);
                else await blogAPI.create(payload);
                toast.success('Article publié ✓');
                setEditModal(false); fetch();
              } catch(err){ toast.error(err.response?.data?.message||'Erreur publication'); }
              finally{ setSaving(false); }
            }}>🌐 Publier</Button>
          )}
          <Button variant="primary" loading={saving} onClick={save}>💾 Enregistrer</Button>
        </div>}
      >
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'var(--space-md)'}}>
          <FormField label="Nom du produit" required><Input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))}/></FormField>
          <FormField label="Référence" required><Input value={form.reference} onChange={e=>setForm(p=>({...p,reference:e.target.value}))}/></FormField>
          <FormField label="Catégorie" required>
            <Select value={form.category_id} onChange={e=>setForm(p=>({...p,category_id:e.target.value}))}>
              <option value="">Choisir…</option>
              {categories.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </FormField>
          <FormField label="Prix de base (€)"><Input type="number" step="0.01" value={form.base_price} onChange={e=>setForm(p=>({...p,base_price:e.target.value}))}/></FormField>
          <FormField label="Nombre de faces">
            <Select value={form.faces_count} onChange={e=>setForm(p=>({...p,faces_count:parseInt(e.target.value)}))}>
              <option value={2}>2 faces</option><option value={3}>3 faces</option>
            </Select>
          </FormField>
          <FormField label="Marque"><Input value={form.brand||''} onChange={e=>setForm(p=>({...p,brand:e.target.value}))}/></FormField>
          <div style={{gridColumn:'1/-1'}}><FormField label="Description courte"><Textarea rows={2} value={form.short_desc||''} onChange={e=>setForm(p=>({...p,short_desc:e.target.value}))}/></FormField></div>
          <div style={{gridColumn:'1/-1'}}><FormField label="Description complète"><Textarea rows={4} value={form.description||''} onChange={e=>setForm(p=>({...p,description:e.target.value}))}/></FormField></div>
          <FormField label="Techniques compatibles">
            <div style={{display:'flex',flexWrap:'wrap',gap:8,padding:'10px',border:'1.5px solid var(--border)',borderRadius:'var(--radius-sm)'}}>
              {techniques.map(t=>(
                <label key={t.id} style={{display:'flex',alignItems:'center',gap:6,fontSize:13,cursor:'pointer'}}>
                  <input type="checkbox" checked={(form.technique_ids||[]).includes(t.id)} onChange={e=>{const ids=form.technique_ids||[];setForm(p=>({...p,technique_ids:e.target.checked?[...ids,t.id]:ids.filter(x=>x!==t.id)}));}}/>
                  {t.name}
                </label>
              ))}
            </div>
          </FormField>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            <label style={{display:'flex',alignItems:'center',gap:8,fontSize:14,cursor:'pointer'}}>
              <input type="checkbox" checked={!!form.is_published} onChange={e=>setForm(p=>({...p,is_published:e.target.checked}))}/> Publié
            </label>
            <label style={{display:'flex',alignItems:'center',gap:8,fontSize:14,cursor:'pointer'}}>
              <input type="checkbox" checked={!!form.is_featured} onChange={e=>setForm(p=>({...p,is_featured:e.target.checked}))}/> Produit à la une
            </label>
          </div>
        </div>
      </Modal>
      <ConfirmDialog isOpen={!!confirm} onClose={()=>setConfirm(null)} onConfirm={()=>del(confirm)} title="Supprimer le produit" message="Cette action est irréversible." confirmLabel="Supprimer" danger/>
    </div>
  );
};

/* ════════════════════════════════════════════════════
   BLOG ADMIN PAGE
   ════════════════════════════════════════════════════ */
export const AdminBlogPage = () => {
  const [posts,    setPosts]   = useState([]);
  const [cats,     setCats]    = useState([]);
  const [meta,     setMeta]    = useState(null);
  const [page,     setPage]    = useState(1);
  const [loading,  setLoading] = useState(true);
  const [editModal,setEditModal]=useState(false);
  const [editing,  setEditing] = useState(null);
  const [saving,   setSaving]  = useState(false);
  const [confirm,  setConfirm] = useState(null);
  const [preview,  setPreview] = useState(false);

  const emptyForm = {title:'',excerpt:'',content:'',featured_image:'',category_id:'',status:'draft',is_featured:false,tags:[],meta_title:'',meta_description:''};
  const [form, setForm] = useState(emptyForm);

  const fetch = useCallback(()=>{
    setLoading(true);
    Promise.all([blogAPI.list({page,per_page:15,status:''}),blogAPI.categories()])
      .then(([p,c])=>{setPosts(p.data.data||[]);setMeta(p.data.meta);setCats(c.data.data||[]);})
      .catch(()=>{}).finally(()=>setLoading(false));
  },[page]);

  useEffect(()=>{fetch();},[fetch]);

  const openEdit = (p)=>{ setEditing(p); setForm({...p,tags:(p.tags||[]).join(', ')}); setEditModal(true); };
  const openNew  = ()=>{ setEditing(null); setForm(emptyForm); setEditModal(true); };

  const save = async ()=>{
    setSaving(true);
    try{
      const payload = {...form, tags: typeof form.tags==='string' ? form.tags.split(',').map(t=>t.trim()).filter(Boolean) : form.tags};
      if(editing) await blogAPI.update(editing.id,payload);
      else        await blogAPI.create(payload);
      toast.success(editing?'Article mis à jour':'Article créé');
      setEditModal(false); fetch();
    } catch(err){ toast.error(err.response?.data?.message||'Erreur'); }
    finally{ setSaving(false); }
  };

  const del = async (id)=>{
    try{ await blogAPI.delete(id); toast.success('Supprimé'); fetch(); } catch{ toast.error('Erreur'); }
    setConfirm(null);
  };

  return (
    <div style={{display:'flex',flexDirection:'column',flex:1}}>
      <div className="admin-page-header">
        <div><h1 className="admin-page-title">Blog</h1><p className="admin-page-subtitle">{meta?.total||0} articles</p></div>
        <Button variant="primary" onClick={openNew}>+ Nouvel article</Button>
      </div>
      <div className="admin-page-content">
        <div className="admin-card">
          {loading?<div className="page-loader"><div className="spinner"/></div>:
            !posts.length?<EmptyState icon="📝" title="Aucun article" action={<Button variant="primary" onClick={openNew}>Créer un article</Button>}/>:
            <table className="data-table">
              <thead><tr><th>Titre</th><th>Catégorie</th><th>Auteur</th><th>Statut</th><th>Vues</th><th>Date</th><th></th></tr></thead>
              <tbody>
                {posts.map(p=>(
                  <tr key={p.id}>
                    <td style={{fontWeight:600,maxWidth:300}}>
                      <div style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.title}</div>
                      {p.is_featured&&<span style={{fontSize:10,fontWeight:700,color:'var(--brand-gold)'}}>⭐ À la une</span>}
                    </td>
                    <td style={{fontSize:13}}>{p.category_name||'—'}</td>
                    <td style={{fontSize:13}}>{p.first_name} {p.last_name}</td>
                    <td><StatusBadge status={p.status}/></td>
                    <td style={{fontSize:13}}>{p.views||0}</td>
                    <td style={{fontSize:12,color:'var(--text-muted)'}}>{p.published_at?new Date(p.published_at).toLocaleDateString('fr-FR'):'—'}</td>
                    <td><div style={{display:'flex',gap:6}}>
                      {p.status !== 'published' && (
                        <Button size="sm" variant="primary" onClick={async()=>{
                          try {
                            await blogAPI.update(p.id, {...p, tags: p.tags||[], status:'published'});
                            toast.success('Article publié ✓');
                            fetch();
                          } catch(e){ toast.error('Erreur'); }
                        }}>🌐 Publier</Button>
                      )}
                      <Button size="sm" variant="secondary" onClick={()=>openEdit(p)}>Modifier</Button>
                      <Button size="sm" variant="ghost" onClick={()=>setConfirm(p.id)}>🗑</Button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
          <div style={{padding:'var(--space-md) var(--space-lg)',borderTop:'1px solid var(--border)'}}><Pagination meta={meta} onPageChange={setPage}/></div>
        </div>
      </div>

      <Modal isOpen={editModal} onClose={()=>setEditModal(false)} title={editing?'Modifier l\'article':'Nouvel article'} size="xl"
        footer={<div style={{display:'flex',gap:8,justifyContent:'flex-end',flexWrap:'wrap'}}>
          <Button variant="ghost" onClick={()=>setEditModal(false)}>Annuler</Button>
          {form.status !== 'published' && (
            <Button variant="secondary" loading={saving} onClick={async()=>{
              setSaving(true);
              try {
                const payload = {...form, status:'published', tags: typeof form.tags==='string' ? form.tags.split(',').map(t=>t.trim()).filter(Boolean) : (form.tags||[])};
                if(editing) await blogAPI.update(editing.id, payload);
                else await blogAPI.create(payload);
                toast.success('Article publié ✓');
                setEditModal(false); fetch();
              } catch(err){ toast.error(err.response?.data?.message||'Erreur publication'); }
              finally{ setSaving(false); }
            }}>🌐 Publier</Button>
          )}
          <Button variant="primary" loading={saving} onClick={save}>💾 Enregistrer</Button>
        </div>}
      >
        <div style={{display:'flex',flexDirection:'column',gap:'var(--space-md)'}}>
          <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:'var(--space-md)'}}>
            <FormField label="Titre" required><Input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))}/></FormField>
            <FormField label="Catégorie">
              <Select value={form.category_id||''} onChange={e=>setForm(p=>({...p,category_id:e.target.value}))}>
                <option value="">Sans catégorie</option>
                {cats.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </FormField>
          </div>
          <FormField label="Extrait"><Textarea rows={2} value={form.excerpt||''} onChange={e=>setForm(p=>({...p,excerpt:e.target.value}))}/></FormField>
          <FormField label="Contenu (HTML autorisé)" required><Textarea rows={12} value={form.content||''} onChange={e=>setForm(p=>({...p,content:e.target.value}))}/></FormField>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'var(--space-md)'}}>
            <FormField label="Image à la une (URL)"><Input value={form.featured_image||''} onChange={e=>setForm(p=>({...p,featured_image:e.target.value}))} placeholder="https://…"/></FormField>
            <FormField label="Tags (séparés par virgule)"><Input value={typeof form.tags==='string'?form.tags:(form.tags||[]).join(', ')} onChange={e=>setForm(p=>({...p,tags:e.target.value}))} placeholder="broderie, t-shirt, conseil"/></FormField>
            <FormField label="Statut" hint={form.status === 'published' ? '🌐 Visible sur le site' : form.status === 'draft' ? '📝 Non visible — brouillon' : '📦 Archivé'}>
              <Select value={form.status} onChange={e=>setForm(p=>({...p,status:e.target.value}))} style={{fontWeight:700, color: form.status==='published'?'#16a34a':'var(--text-primary)'}}>
                {[['draft','📝 Brouillon'],['published','🌐 Publié (visible)'],['archived','📦 Archivé']].map(([v,l])=><option key={v} value={v}>{l}</option>)}
              </Select>
            </FormField>
            <div style={{display:'flex',alignItems:'flex-end',gap:8,paddingBottom:2}}>
              <label style={{display:'flex',alignItems:'center',gap:8,fontSize:14,cursor:'pointer'}}>
                <input type="checkbox" checked={!!form.is_featured} onChange={e=>setForm(p=>({...p,is_featured:e.target.checked}))}/> Article à la une
              </label>
            </div>
          </div>
        </div>
      </Modal>
      <ConfirmDialog isOpen={!!confirm} onClose={()=>setConfirm(null)} onConfirm={()=>del(confirm)} title="Supprimer l'article" message="Cette action est irréversible." confirmLabel="Supprimer" danger/>

      {/* ── Modal aperçu article ── */}
      <Modal isOpen={preview} onClose={()=>setPreview(false)} title="Aperçu de l'article" size="xl"
        footer={<div style={{display:'flex',gap:8,justifyContent:'flex-end'}}><Button variant="ghost" onClick={()=>setPreview(false)}>Fermer</Button></div>}
      >
        <div style={{maxWidth:720,margin:'0 auto',fontFamily:'inherit'}}>
          {form.featured_image && (
            <img src={form.featured_image} alt="" style={{width:'100%',height:280,objectFit:'cover',borderRadius:'var(--radius-lg)',marginBottom:'var(--space-lg)'}} onError={e=>e.target.style.display='none'}/>
          )}
          <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:'var(--space-md)'}}>
            <span style={{fontSize:12,fontWeight:700,color:'var(--brand-accent)',textTransform:'uppercase',letterSpacing:1}}>
              {cats.find(c=>String(c.id)===String(form.category_id))?.name || 'Article'}
            </span>
            <span style={{fontSize:12,color:'var(--text-muted)'}}>•</span>
            <span style={{fontSize:12,color:'var(--text-muted)'}}>
              {form.status==='published'?'🌐 Publié':'📝 Brouillon'}
            </span>
          </div>
          <h1 style={{fontSize:'clamp(22px,3vw,36px)',fontWeight:800,lineHeight:1.2,marginBottom:'var(--space-md)'}}>{form.title||'(Sans titre)'}</h1>
          {form.excerpt && <p style={{fontSize:17,color:'var(--text-secondary)',lineHeight:1.7,marginBottom:'var(--space-lg)',borderLeft:'3px solid var(--brand-accent)',paddingLeft:16}}>{form.excerpt}</p>}
          <div
            style={{fontSize:15,lineHeight:1.8,color:'var(--text-secondary)'}}
            dangerouslySetInnerHTML={{__html: form.content || '<p style="color:var(--text-muted);font-style:italic">Aucun contenu</p>'}}
          />
          {form.tags && (
            <div style={{marginTop:'var(--space-xl)',display:'flex',gap:8,flexWrap:'wrap'}}>
              {(typeof form.tags==='string'?form.tags.split(',').map(t=>t.trim()).filter(Boolean):form.tags||[]).map(t=>(
                <span key={t} style={{padding:'3px 10px',background:'var(--bg-page)',border:'1px solid var(--border)',borderRadius:'var(--radius-full)',fontSize:12}}>{t}</span>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

/* ════════════════════════════════════════════════════
   SETTINGS PAGE
   ════════════════════════════════════════════════════ */
export const AdminSettingsPage = () => {
  const [settings,  setSettings]  = useState({});
  const [users,     setUsers]     = useState([]);
  const [tab,       setTab]       = useState('general');
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [userModal, setUserModal] = useState(false);
  const [editUser,  setEditUser]  = useState(null);
  const [userForm,  setUserForm]  = useState({first_name:'',last_name:'',email:'',password:'',role:'support',is_active:true});
  const [savingUser,setSavingUser]= useState(false);
  const [confirmUser, setConfirmUser] = useState(null);

  useEffect(()=>{
    Promise.all([settingsAPI.all(),settingsAPI.users()])
      .then(([s,u])=>{
        const flat={};
        Object.values(s.data.data||{}).forEach(arr=>arr.forEach(item=>{flat[item.key]=item.value;}));
        setSettings(flat);
        setUsers(u.data.data||[]);
      }).catch(()=>{}).finally(()=>setLoading(false));
  },[]);

  const save = async ()=>{
    setSaving(true);
    try{ await settingsAPI.update(settings); toast.success('Paramètres sauvegardés'); } catch{ toast.error('Erreur'); }
    finally{ setSaving(false); }
  };

  const saveUser = async ()=>{
    setSavingUser(true);
    try{
      if(editUser) await settingsAPI.updateUser(editUser.id,userForm);
      else         await settingsAPI.createUser(userForm);
      toast.success(editUser?'Utilisateur mis à jour':'Utilisateur créé');
      setUserModal(false);
      settingsAPI.users().then(({data})=>setUsers(data.data||[]));
    } catch(err){ toast.error(err.response?.data?.message||'Erreur'); }
    finally{ setSavingUser(false); }
  };

  const deleteUser = async (id) => {
    try {
      await settingsAPI.deleteUser(id);
      toast.success('Utilisateur supprimé');
      settingsAPI.users().then(({ data }) => setUsers(data.data || []));
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    setConfirmUser(null);
  };

  const tabs = [['general','Général'],['homepage','Accueil'],['billing','Facturation'],['email','Email'],['legal','Légal'],['social','Réseaux'],['users','Utilisateurs']];

  const roleLabels={super_admin:'Super Admin',admin:'Admin',collaborator:'Collaborateur',support:'Support'};
  const roleBadge={super_admin:'badge-error',admin:'badge-accent',collaborator:'badge-info',support:'badge-neutral'};

  // SettingInput défini comme helper (pas composant) pour éviter la perte de focus
  const si = (k, label, type='text', hint) => (
    <FormField label={label} hint={hint}>
      {type==='textarea'
        ? <Textarea value={settings[k]||''} onChange={e=>setSettings(p=>({...p,[k]:e.target.value}))} rows={6}/>
        : <Input type={type} value={settings[k]||''} onChange={e=>setSettings(p=>({...p,[k]:e.target.value}))}/>
      }
    </FormField>
  );

  if (loading) return <div className="page-loader" style={{flex:1}}><div className="spinner spinner-lg"/></div>;

  return (
    <div style={{display:'flex',flexDirection:'column',flex:1}}>
      <div className="admin-page-header">
        <div><h1 className="admin-page-title">Paramètres</h1><p className="admin-page-subtitle">Configuration de votre site</p></div>
        {tab!=='users'&&<Button variant="primary" loading={saving} onClick={save}>Enregistrer</Button>}
      </div>
      <div className="admin-page-content">
        <div style={{display:'flex',gap:'var(--space-xl)',alignItems:'flex-start'}}>
          {/* Tab nav */}
          <div style={{display:'flex',flexDirection:'column',gap:2,minWidth:160,flexShrink:0}}>
            {tabs.map(([key,label])=>(
              <button key={key} onClick={()=>setTab(key)} style={{padding:'9px 14px',borderRadius:'var(--radius-sm)',textAlign:'left',fontSize:13,fontWeight:600,background:tab===key?'rgba(230,57,70,.07)':'transparent',color:tab===key?'var(--brand-accent)':'var(--text-secondary)',border:'none',cursor:'pointer',transition:'all .2s'}}>{label}</button>
            ))}
          </div>

          {/* Tab content */}
          <div style={{flex:1,display:'flex',flexDirection:'column',gap:'var(--space-md)'}}>
            {tab==='general'&&(
              <div className="admin-card" style={{padding:'var(--space-xl)',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'var(--space-md)'}}>
                {si('site_name','Nom du site')}
                {si('site_tagline','Slogan')}
                {si('site_email','Email de contact','email')}
                {si('site_phone','Téléphone')}
                <div style={{gridColumn:'1/-1'}}>{si('site_address','Adresse')}</div>
                {/* Logos */}
                <div style={{gridColumn:'1/-1',borderTop:'1px solid var(--border)',paddingTop:'var(--space-md)',marginTop:4}}>
                  <div style={{fontSize:13,fontWeight:700,color:'var(--text-secondary)',marginBottom:'var(--space-md)',textTransform:'uppercase',letterSpacing:.5}}>🖼 Logos du site</div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'var(--space-md)'}}>
                    <div>
                      <FormField label="Logo — fond clair" hint="Header scrollé, backoffice">
                        <Input value={settings['site_logo']||''} onChange={e=>setSettings(p=>({...p,site_logo:e.target.value}))} placeholder="https://…/logo-dark.png"/>
                      </FormField>
                      {settings['site_logo']&&<div style={{marginTop:8,padding:10,background:'#fff',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',display:'flex',alignItems:'center',justifyContent:'center',minHeight:60}}><img src={settings['site_logo']} alt="" style={{maxHeight:40,maxWidth:'100%',objectFit:'contain'}} onError={e=>e.target.style.display='none'}/></div>}
                    </div>
                    <div>
                      <FormField label="Logo — fond sombre" hint="Header transparent, footer">
                        <Input value={settings['site_logo_dark']||''} onChange={e=>setSettings(p=>({...p,site_logo_dark:e.target.value}))} placeholder="https://…/logo-white.png"/>
                      </FormField>
                      {settings['site_logo_dark']&&<div style={{marginTop:8,padding:10,background:'#1e293b',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',display:'flex',alignItems:'center',justifyContent:'center',minHeight:60}}><img src={settings['site_logo_dark']} alt="" style={{maxHeight:40,maxWidth:'100%',objectFit:'contain'}} onError={e=>e.target.style.display='none'}/></div>}
                    </div>
                    <div>
                      <FormField label="Logo — backoffice" hint="Sidebar admin et connexion">
                        <Input value={settings['site_logo_admin']||''} onChange={e=>setSettings(p=>({...p,site_logo_admin:e.target.value}))} placeholder="https://…/logo-admin.png"/>
                      </FormField>
                      {settings['site_logo_admin']&&<div style={{marginTop:8,padding:10,background:'#f8fafc',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',display:'flex',alignItems:'center',justifyContent:'center',minHeight:60}}><img src={settings['site_logo_admin']} alt="" style={{maxHeight:40,maxWidth:'100%',objectFit:'contain'}} onError={e=>e.target.style.display='none'}/></div>}
                    </div>
                  </div>
                </div>
                {si('site_favicon','Favicon (URL)','text','Icône onglet (32×32px)')}
              </div>
            )}
            {tab==='homepage'&&(
              <div className="admin-card" style={{padding:'var(--space-xl)',display:'flex',flexDirection:'column',gap:'var(--space-md)'}}>
                {si('hero_title','Titre principal (hero)')}
                {si('hero_subtitle','Sous-titre')}
                {si('hero_image','Image hero (URL)')}
              </div>
            )}
            {tab==='billing'&&(
              <div className="admin-card" style={{padding:'var(--space-xl)',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'var(--space-md)'}}>
                {si('vat_rate','Taux TVA (%)','number')}
                {si('currency_symbol','Symbole devise')}
                {si('quote_prefix','Préfixe devis','text','ex: DEV-')}
                {si('invoice_prefix','Préfixe facture','text','ex: FAC-')}
                {si('quote_validity_days','Validité devis (jours)','number')}
              </div>
            )}
            {tab==='email'&&(
              <div className="admin-card" style={{padding:'var(--space-xl)',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'var(--space-md)'}}>
                {si('smtp_host','SMTP Host')}
                {si('smtp_port','SMTP Port','number')}
                {si('smtp_user','SMTP User')}
                <FormField label="SMTP Password"><PasswordInput value={settings['smtp_pass']||''} onChange={e=>setSettings(p=>({...p,smtp_pass:e.target.value}))} autoComplete="new-password"/></FormField>
                {si('smtp_from_name','Nom expéditeur')}
                {si('smtp_from_email','Email expéditeur','email')}
              </div>
            )}
            {tab==='legal'&&(
              <div className="admin-card" style={{padding:'var(--space-xl)',display:'flex',flexDirection:'column',gap:'var(--space-md)'}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'var(--space-md)'}}>
                  {si('legal_company','Raison sociale')}
                  {si('legal_siret','SIRET')}
                  {si('legal_rcs','RCS')}
                  {si('legal_capital','Capital social')}
                  {si('legal_director','Directeur de publication')}
                  {si('legal_host','Hébergeur')}
                </div>
                {si('legal_content','Mentions légales (HTML)','textarea')}
                {si('cgv_content','CGV (HTML)','textarea')}
                {si('privacy_content','Protection des données (HTML)','textarea')}
                {si('cookies_content','Politique cookies (HTML)','textarea')}
              </div>
            )}
            {tab==='social'&&(
              <div className="admin-card" style={{padding:'var(--space-xl)',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'var(--space-md)'}}>
                {si('social_facebook','URL Facebook')}
                {si('social_instagram','URL Instagram')}
                {si('social_linkedin','URL LinkedIn')}
                {si('google_analytics_id','Google Analytics ID','text','G-XXXXXXXX')}
                {si('facebook_pixel_id','Facebook Pixel ID')}
              </div>
            )}
            {tab==='users'&&(
              <div className="admin-card">
                <div className="admin-card-header">
                  <h3 style={{fontWeight:700,fontSize:15}}>Utilisateurs ({users.length})</h3>
                  <Button variant="primary" size="sm" onClick={()=>{setEditUser(null);setUserForm({first_name:'',last_name:'',email:'',password:'',role:'support',is_active:true});setUserModal(true);}}>+ Ajouter</Button>
                </div>
                <table className="data-table">
                  <thead><tr><th>Nom</th><th>Email</th><th>Rôle</th><th>Dernière connexion</th><th>Statut</th><th></th></tr></thead>
                  <tbody>
                    {users.map(u=>(
                      <tr key={u.id}>
                        <td style={{fontWeight:600}}>{u.first_name} {u.last_name}</td>
                        <td style={{fontSize:13,color:'var(--text-muted)'}}>{u.email}</td>
                        <td><span className={`badge ${roleBadge[u.role]||'badge-neutral'}`}>{roleLabels[u.role]||u.role}</span></td>
                        <td style={{fontSize:12,color:'var(--text-muted)'}}>{u.last_login_at?new Date(u.last_login_at).toLocaleDateString('fr-FR'):'Jamais'}</td>
                        <td><span className={`badge ${u.is_active?'badge-success':'badge-neutral'}`}>{u.is_active?'Actif':'Inactif'}</span></td>
                        <td>
                          <div style={{display:'flex',gap:6}}>
                            <Button size="sm" variant="secondary" onClick={()=>{setEditUser(u);setUserForm({...u,password:''});setUserModal(true);}}>Modifier</Button>
                            <Button size="sm" variant="ghost" onClick={()=>setConfirmUser(u.id)} title="Supprimer">🗑</Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal isOpen={userModal} onClose={()=>setUserModal(false)} title={editUser?'Modifier l\'utilisateur':'Nouvel utilisateur'}
        footer={<div style={{display:'flex',gap:8,justifyContent:'flex-end'}}><Button variant="ghost" onClick={()=>setUserModal(false)}>Annuler</Button><Button variant="primary" loading={savingUser} onClick={saveUser}>Enregistrer</Button></div>}
      >
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'var(--space-md)'}}>
          <FormField label="Prénom"><Input value={userForm.first_name} onChange={e=>setUserForm(p=>({...p,first_name:e.target.value}))}/></FormField>
          <FormField label="Nom"><Input value={userForm.last_name} onChange={e=>setUserForm(p=>({...p,last_name:e.target.value}))}/></FormField>
          <FormField label="Email" required><Input type="email" value={userForm.email} onChange={e=>setUserForm(p=>({...p,email:e.target.value}))}/></FormField>
          <FormField label={editUser?"Nouveau mot de passe":"Mot de passe"} required={!editUser}><PasswordInput value={userForm.password||''} onChange={e=>setUserForm(p=>({...p,password:e.target.value}))} placeholder={editUser?"Laisser vide pour ne pas changer":"Min. 8 caractères"} autoComplete="new-password"/></FormField>
          <FormField label="Rôle">
            <Select value={userForm.role} onChange={e=>setUserForm(p=>({...p,role:e.target.value}))}>
              {Object.entries(roleLabels).map(([v,l])=><option key={v} value={v}>{l}</option>)}
            </Select>
          </FormField>
          <FormField label="Statut">
            <Select value={userForm.is_active?'1':'0'} onChange={e=>setUserForm(p=>({...p,is_active:e.target.value==='1'}))}>
              <option value="1">Actif</option><option value="0">Inactif</option>
            </Select>
          </FormField>
        </div>
      </Modal>
      <ConfirmDialog isOpen={!!confirmUser} onClose={()=>setConfirmUser(null)} onConfirm={()=>deleteUser(confirmUser)} title="Supprimer l'utilisateur" message="Cet utilisateur sera définitivement supprimé et ne pourra plus se connecter." confirmLabel="Supprimer" danger/>
    </div>
  );
};